import {
  Button,
  Callout,
  Card,
  ObsinaProvider,
  Wordmark,
} from "@obsina/design-system";
import {
  createClient,
  type Session,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { App } from "./App";
import type { AuthenticatedAccount } from "./App";
import {
  bootstrapApplicationOwner,
  clearApplicationSession,
  establishApplicationSession,
  isTenantSelectionRequired,
  listTenantMemberships,
  normalizeApiBase,
  selectTenant,
} from "./api";
import type { TenantMembership } from "./api";
import { APP_DESIGN_SYSTEM_MESSAGES, APP_LOCALE } from "./locale";
import { enMessages } from "./messages";

type SignInProvider = "microsoft" | "google";

export interface PublicAuthConfig {
  readonly supabaseUrl: string;
  readonly supabasePublishableKey: string;
}

export function shouldBypassAuthenticationForE2e(environment: {
  readonly DEV: boolean;
  readonly MODE: string;
  readonly VITE_OBSINA_E2E_AUTH_BYPASS?: string;
}): boolean {
  return (
    environment.DEV &&
    environment.MODE === "e2e" &&
    environment.VITE_OBSINA_E2E_AUTH_BYPASS === "true"
  );
}

export interface SessionSynchronizer<
  T extends { readonly access_token: string },
> {
  (session: T): Promise<void>;
  drain(): Promise<void>;
  pause(): void;
  reset(): void;
  resume(): void;
}

export const APPLICATION_SESSION_RENEW_AFTER_MS = 10 * 60_000;
export const APPLICATION_SESSION_CHECK_INTERVAL_MS = 60_000;

export function createSessionSynchronizer<
  T extends { readonly access_token: string },
>(
  run: (session: T) => Promise<void>,
  options: {
    readonly now?: () => number;
    readonly renewAfterMs?: number;
  } = {},
): SessionSynchronizer<T> {
  const now = options.now ?? Date.now;
  const renewAfterMs =
    options.renewAfterMs ?? APPLICATION_SESSION_RENEW_AFTER_MS;
  if (!Number.isSafeInteger(renewAfterMs) || renewAfterMs <= 0) {
    throw new Error("The application session renewal interval is invalid.");
  }
  const completedAccessTokens = new Map<string, number>();
  let pendingAccessToken: string | null = null;
  let pending: Promise<void> | null = null;
  let paused = false;

  const completedRecently = (accessToken: string): boolean => {
    const completedAt = completedAccessTokens.get(accessToken);
    if (completedAt === undefined) return false;
    const elapsed = now() - completedAt;
    return elapsed >= 0 && elapsed < renewAfterMs;
  };

  const synchronize = async (session: T): Promise<void> => {
    if (paused) return;
    if (completedRecently(session.access_token)) return;
    while (pending) {
      if (session.access_token === pendingAccessToken) return pending;
      // A newer token must still get its own chance when the preceding token
      // expires or fails verification. The original caller retains that
      // failure; this caller continues once the shared slot is released.
      await pending.catch(() => undefined);
    }
    if (paused) return;
    if (completedRecently(session.access_token)) return;

    const task = run(session);
    pending = task;
    pendingAccessToken = session.access_token;
    try {
      await task;
      completedAccessTokens.set(session.access_token, now());
    } finally {
      if (pending === task) {
        pending = null;
        pendingAccessToken = null;
      }
    }
  };

  return Object.assign(synchronize, {
    drain: async (): Promise<void> => {
      const active = pending;
      if (active) await active.catch(() => undefined);
    },
    pause: (): void => {
      paused = true;
    },
    reset: (): void => {
      completedAccessTokens.clear();
      pendingAccessToken = null;
    },
    resume: (): void => {
      paused = false;
    },
  });
}

export async function synchronizeCurrentSession<
  T extends { readonly access_token: string },
>(
  candidate: T,
  loadCurrent: () => Promise<T | null>,
  synchronize: (session: T) => Promise<void>,
): Promise<"applied" | "stale"> {
  const matchesCurrent = async (): Promise<boolean> =>
    (await loadCurrent())?.access_token === candidate.access_token;

  if (!(await matchesCurrent())) return "stale";
  try {
    await synchronize(candidate);
  } catch (error) {
    // A failed obsolete callback must not replace a newer successful session
    // with the recovery screen. Preserve the original failure only while this
    // candidate is still the session Supabase actually stores.
    try {
      if (!(await matchesCurrent())) return "stale";
    } catch {
      // A failed current-session lookup cannot prove that the callback is old.
    }
    throw error;
  }
  return (await matchesCurrent()) ? "applied" : "stale";
}

export async function signOutAfterSessionSynchronization<
  T extends { readonly access_token: string },
>(
  synchronizer: SessionSynchronizer<T> | null,
  action: () => Promise<void>,
): Promise<void> {
  synchronizer?.pause();
  try {
    await synchronizer?.drain();
    await action();
    synchronizer?.reset();
  } finally {
    synchronizer?.resume();
  }
}

export function createApplicationSessionSynchronizer(
  apiBase: string,
): SessionSynchronizer<Session> {
  return createSessionSynchronizer(async (session: Session) => {
    await establishApplicationSession(apiBase, session.access_token);
    await bootstrapApplicationOwner(apiBase, session.user.id);
  });
}

export function readPublicAuthConfig(environment: {
  readonly VITE_OBSINA_SUPABASE_URL?: string;
  readonly VITE_OBSINA_SUPABASE_PUBLISHABLE_KEY?: string;
}): PublicAuthConfig | null {
  const rawUrl = environment.VITE_OBSINA_SUPABASE_URL?.trim();
  const publishableKey =
    environment.VITE_OBSINA_SUPABASE_PUBLISHABLE_KEY?.trim();
  if (!rawUrl || !publishableKey || publishableKey.length < 20) return null;
  try {
    const url = new URL(rawUrl);
    if (
      url.protocol !== "https:" ||
      !url.hostname.endsWith(".supabase.co") ||
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    ) {
      return null;
    }
    return {
      supabaseUrl: url.origin,
      supabasePublishableKey: publishableKey,
    };
  } catch {
    return null;
  }
}

function previewState(): string | null {
  return new URLSearchParams(window.location.search).get("preview");
}

export function AuthenticatedRoot() {
  const apiBase = normalizeApiBase(import.meta.env.VITE_OBSINA_API_BASE_URL);

  // Dev-only visual preview, the same mechanism SupabaseAuthenticatedRoot uses
  // below: `?preview=tenant-selection` renders the organisation chooser against
  // fixture names so its density can be measured in a real browser. Never
  // active in a production build, and it grants no data access — choosing does
  // nothing here.
  if (import.meta.env.DEV && previewState() === "tenant-selection") {
    return (
      <TenantSelection
        error={null}
        memberships={[
          {
            tenantId: "20000000-0000-4000-8000-0000000000aa",
            tenantName: "Isotec Solar",
            role: "owner",
          },
          {
            tenantId: "20000000-0000-4000-8000-0000000000bb",
            tenantName: "Isotec Enerji Sistemleri",
            role: "member",
          },
        ]}
        onSelect={() => undefined}
        onSignOut={() => undefined}
        pendingTenantId={null}
      />
    );
  }

  if (shouldBypassAuthenticationForE2e(import.meta.env)) {
    return <App apiBase={apiBase} />;
  }

  return <SupabaseAuthenticatedRoot apiBase={apiBase} />;
}

function SupabaseAuthenticatedRoot({
  apiBase,
}: {
  readonly apiBase: string | null;
}) {
  const authConfig = useMemo(() => readPublicAuthConfig(import.meta.env), []);
  // Dev-only visual preview: `?preview=<state>` renders the control-plane shell
  // against a fixture summary, bypassing the Supabase auth/config gate so the UI
  // can be worked on locally without staging credentials. Never active in a
  // production build (import.meta.env.DEV), and the query param is dev-tooling
  // only — it grants no data access (apiBase is null, so nothing is fetched).
  const preview = useMemo(
    () => (import.meta.env.DEV ? previewState() : null),
    [],
  );
  const supabase = useMemo(
    () =>
      authConfig
        ? createClient(
            authConfig.supabaseUrl,
            authConfig.supabasePublishableKey,
            {
              auth: {
                persistSession: true,
                autoRefreshToken: true,
                detectSessionInUrl: true,
                flowType: "pkce",
                storage: providerTokenStrippingStorage(window.localStorage),
              },
            },
          )
        : null,
    [authConfig],
  );
  const [state, setState] = useState<
    "loading" | "signed_out" | "signed_in" | "tenant_selection" | "error"
  >("loading");
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<AuthenticatedAccount | null>(null);
  const [sessionRevision, setSessionRevision] = useState(0);
  const [recoveryAction, setRecoveryAction] = useState<"sign_out" | null>(null);
  const sessionSynchronizer = useMemo(
    () => (apiBase ? createApplicationSessionSynchronizer(apiBase) : null),
    [apiBase],
  );
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (!apiBase || !supabase || !sessionSynchronizer) {
      setState("error");
      return;
    }
    let active = true;
    const synchronize = async (session: Session | null): Promise<void> => {
      if (!active || signingOutRef.current) return;
      if (!session) {
        const { data: current, error: currentSessionError } =
          await supabase.auth.getSession();
        if (currentSessionError) {
          if (active && !signingOutRef.current) {
            setError("Güvenli uygulama oturumu kurulamadı.");
            setState("error");
          }
          return;
        }
        if (current.session !== null) return;
        setAccount(null);
        setState("signed_out");
        return;
      }
      try {
        const result = await synchronizeCurrentSession(
          session,
          async () => {
            const { data, error: currentSessionError } =
              await supabase.auth.getSession();
            if (currentSessionError) throw currentSessionError;
            return data.session;
          },
          sessionSynchronizer,
        );
        if (result === "stale") return;
        if (active && !signingOutRef.current) {
          setAccount(accountFromSession(session));
          setError(null);
          setState("signed_in");
        }
      } catch (synchronizationError) {
        if (!active || signingOutRef.current) return;
        // A session that belongs to two organisations is not a broken session.
        // Ask which one, rather than dropping the user onto the recovery
        // screen with nothing to act on.
        if (isTenantSelectionRequired(synchronizationError)) {
          setAccount(accountFromSession(session));
          setError(null);
          setState("tenant_selection");
          return;
        }
        setError("Güvenli uygulama oturumu kurulamadı.");
        setState("error");
      }
    };
    void supabase.auth
      .getSession()
      .then(({ data }) => synchronize(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void synchronize(session);
    });
    const renewCurrentSession = (): void => {
      void supabase.auth
        .getSession()
        .then(({ data: current, error: currentSessionError }) => {
          if (!currentSessionError && active) {
            return synchronize(current.session);
          }
        })
        .catch(() => undefined);
    };
    const renewalTimer = window.setInterval(
      renewCurrentSession,
      APPLICATION_SESSION_CHECK_INTERVAL_MS,
    );
    const renewOnVisible = (): void => {
      if (document.visibilityState === "visible") renewCurrentSession();
    };
    window.addEventListener("focus", renewCurrentSession);
    document.addEventListener("visibilitychange", renewOnVisible);
    return () => {
      active = false;
      window.clearInterval(renewalTimer);
      window.removeEventListener("focus", renewCurrentSession);
      document.removeEventListener("visibilitychange", renewOnVisible);
      data.subscription.unsubscribe();
    };
  }, [apiBase, sessionRevision, sessionSynchronizer, supabase]);

  const safelySignOut = async (): Promise<void> => {
    if (!apiBase || !supabase) return;
    signingOutRef.current = true;
    try {
      await signOutAfterSessionSynchronization(sessionSynchronizer, () =>
        signOut(apiBase, supabase),
      );
      setAccount(null);
      setError(null);
      setState("signed_out");
    } finally {
      signingOutRef.current = false;
    }
  };

  if (preview) {
    return <App account={null} apiBase={null} onSignOut={() => undefined} />;
  }

  if (!apiBase || !supabase || !sessionSynchronizer) {
    return (
      <AuthFrame>
        <Callout title={enMessages.auth.secureUnavailableTitle} tone="danger">
          {enMessages.auth.stagingConfigIncomplete}
        </Callout>
      </AuthFrame>
    );
  }
  if (state === "error") {
    return (
      <SessionRecovery
        error={error ?? enMessages.auth.sessionCouldNotEstablish}
        onRetry={() => {
          sessionSynchronizer.reset();
          setError(null);
          setState("loading");
          setSessionRevision((value) => value + 1);
        }}
        onSignOut={() => {
          if (recoveryAction) return;
          setRecoveryAction("sign_out");
          void safelySignOut()
            .catch(() => {
              setError(enMessages.auth.signOutClearFailed);
            })
            .finally(() => setRecoveryAction(null));
        }}
        signingOut={recoveryAction === "sign_out"}
      />
    );
  }
  if (state === "loading") {
    return (
      <AuthFrame>
        <p role="status">{enMessages.auth.establishingSession}</p>
      </AuthFrame>
    );
  }
  if (state === "tenant_selection") {
    return (
      <TenantSelectionScreen
        apiBase={apiBase}
        onSelected={() => {
          sessionSynchronizer.reset();
          setError(null);
          setState("loading");
          setSessionRevision((value) => value + 1);
        }}
        onSignOut={() => void safelySignOut()}
      />
    );
  }
  if (state === "signed_out") {
    return (
      <SignIn
        onSignInWithGoogle={() => signInWithGoogle(supabase, setError)}
        onSignInWithMicrosoft={() => signInWithMicrosoft(supabase, setError)}
        error={error}
      />
    );
  }
  return (
    <App
      account={account}
      apiBase={apiBase}
      onSignOut={safelySignOut}
      // The consent bridge is the one surface that presents the member's own
      // access token to a host other than the API. It reads it from here — the
      // client that already owns the session — rather than constructing a
      // second Supabase client over the same storage, which worked but put two
      // owners on one session for no benefit.
      readAccessToken={async () => {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) return null;
        return data.session?.access_token ?? null;
      }}
    />
  );
}

export function SessionRecovery({
  error,
  onRetry,
  onSignOut,
  signingOut,
}: {
  readonly error: string;
  readonly onRetry: () => void;
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
}) {
  return (
    <AuthFrame>
      <Callout
        announce
        title={enMessages.auth.secureUnavailableTitle}
        tone="danger"
      >
        <div className="auth-recovery">
          <p>{error}</p>
          <div className="auth-recovery__actions">
            <Button disabled={signingOut} onClick={onRetry} type="button">
              {enMessages.auth.retrySecureSession}
            </Button>
            <Button
              disabled={signingOut}
              loading={signingOut}
              loadingLabel={enMessages.auth.signingOut}
              onClick={onSignOut}
              type="button"
              variant="secondary"
            >
              {enMessages.auth.signOut}
            </Button>
          </div>
        </div>
      </Callout>
    </AuthFrame>
  );
}

function TenantSelectionScreen({
  apiBase,
  onSelected,
  onSignOut,
}: {
  readonly apiBase: string;
  readonly onSelected: () => void;
  readonly onSignOut: () => void;
}) {
  const [memberships, setMemberships] = useState<
    readonly TenantMembership[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listTenantMemberships(apiBase, controller.signal)
      .then((loaded) => setMemberships(loaded))
      .catch(() => {
        if (!controller.signal.aborted) {
          setError(enMessages.auth.organisationsUnavailable);
        }
      });
    return () => controller.abort();
  }, [apiBase]);

  const choose = (tenantId: string): void => {
    if (pendingTenantId) return;
    setPendingTenantId(tenantId);
    setError(null);
    selectTenant(apiBase, tenantId)
      .then(onSelected)
      .catch(() => {
        setError(enMessages.auth.organisationSelectFailed);
        setPendingTenantId(null);
      });
  };

  return (
    <TenantSelection
      error={error}
      memberships={memberships}
      onSelect={choose}
      onSignOut={onSignOut}
      pendingTenantId={pendingTenantId}
    />
  );
}

/**
 * One decision, one card, and the card is only as wide as what it asks. The
 * buttons take the width of the longest organisation name rather than the
 * width of the screen, and share it so the column keeps a straight edge.
 */
export function TenantSelection({
  error,
  memberships,
  onSelect,
  onSignOut,
  pendingTenantId,
}: {
  readonly error: string | null;
  readonly memberships: readonly TenantMembership[] | null;
  readonly onSelect: (tenantId: string) => void;
  readonly onSignOut: () => void;
  readonly pendingTenantId: string | null;
}) {
  const messages = enMessages.auth;
  return (
    <AuthFrame>
      <Card style={{ width: "fit-content", maxWidth: "100%" }}>
        <div className="auth-form">
          <div>
            <h1>{messages.organisationChoiceTitle}</h1>
            <p>{messages.organisationChoiceBody}</p>
          </div>
          {error && (
            <Callout
              announce
              title={messages.organisationChoiceFailedTitle}
              tone="danger"
            >
              {error}
            </Callout>
          )}
          {memberships === null ? (
            <p role="status">{messages.organisationsLoading}</p>
          ) : (
            <div
              aria-label={messages.organisationsGroupLabel}
              role="group"
              style={{
                display: "grid",
                gap: "var(--obsina-space-2)",
                justifyContent: "start",
                width: "fit-content",
                maxWidth: "100%",
              }}
            >
              {memberships.map((membership) => (
                <Button
                  disabled={
                    pendingTenantId !== null &&
                    pendingTenantId !== membership.tenantId
                  }
                  key={membership.tenantId}
                  loading={pendingTenantId === membership.tenantId}
                  loadingLabel={messages.organisationOpening}
                  onClick={() => onSelect(membership.tenantId)}
                  type="button"
                  variant="secondary"
                >
                  {membership.tenantName ?? messages.organisationUnnamed}
                </Button>
              ))}
            </div>
          )}
          <div>
            <Button onClick={onSignOut} size="sm" type="button" variant="ghost">
              {messages.signOut}
            </Button>
          </div>
        </div>
      </Card>
    </AuthFrame>
  );
}

function SignIn({
  onSignInWithGoogle,
  onSignInWithMicrosoft,
  error,
}: {
  readonly onSignInWithGoogle: () => Promise<void>;
  readonly onSignInWithMicrosoft: () => Promise<void>;
  readonly error: string | null;
}) {
  const [pendingProvider, setPendingProvider] = useState<SignInProvider | null>(
    null,
  );
  const messages = enMessages.auth;

  const submit = (
    provider: SignInProvider,
    action: () => Promise<void>,
  ): void => {
    if (pendingProvider) return;
    setPendingProvider(provider);
    void action().finally(() => setPendingProvider(null));
  };

  return (
    <AuthFrame>
      <Card>
        <div className="auth-form">
          <div>
            <p className="eyebrow">{messages.eyebrow}</p>
            <h1>{messages.title}</h1>
            <p>{messages.body}</p>
          </div>
          {error && (
            <Callout announce title={messages.signInFailedTitle} tone="danger">
              {error}
            </Callout>
          )}
          <div
            aria-label={messages.providersGroupLabel}
            role="group"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--obsina-space-3)",
            }}
          >
            <Button
              disabled={pendingProvider !== null}
              fullWidth
              iconEnd={
                <ArrowRight
                  aria-hidden="true"
                  size={17}
                  style={{ marginInlineStart: "auto" }}
                />
              }
              iconStart={<MicrosoftMark />}
              loading={pendingProvider === "microsoft"}
              loadingLabel={messages.microsoftConnecting}
              onClick={() => submit("microsoft", onSignInWithMicrosoft)}
              type="button"
              variant="secondary"
            >
              <ProviderLabel
                label={messages.microsoftLabel}
                sub={messages.microsoftSub}
              />
            </Button>
            <Button
              disabled={pendingProvider !== null}
              fullWidth
              iconEnd={
                <ArrowRight
                  aria-hidden="true"
                  size={17}
                  style={{ marginInlineStart: "auto" }}
                />
              }
              iconStart={<GoogleMark />}
              loading={pendingProvider === "google"}
              loadingLabel={messages.googleConnecting}
              onClick={() => submit("google", onSignInWithGoogle)}
              type="button"
              variant="secondary"
            >
              <ProviderLabel
                label={messages.googleLabel}
                sub={messages.googleSub}
              />
            </Button>
          </div>
          <p className="obsina-sr-only" role="status">
            {pendingProvider &&
              messages.connectingAnnouncement(
                pendingProvider === "microsoft" ? "Microsoft" : "Google",
              )}
          </p>
          <p className="auth-privacy-note">{messages.privacyNote}</p>
        </div>
      </Card>
    </AuthFrame>
  );
}

function ProviderLabel({
  label,
  sub,
}: {
  readonly label: string;
  readonly sub: string;
}) {
  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.125rem",
      }}
    >
      <span>{label}</span>
      <span
        style={{
          fontWeight: 400,
          fontSize: "var(--obsina-text-xs)",
          color: "var(--obsina-text-muted)",
        }}
      >
        {sub}
      </span>
    </span>
  );
}

function MicrosoftMark() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="20"
      viewBox="0 0 21 21"
      width="20"
    >
      <rect fill="#F25022" height="9" width="9" x="1" y="1" />
      <rect fill="#7FBA00" height="9" width="9" x="11" y="1" />
      <rect fill="#00A4EF" height="9" width="9" x="1" y="11" />
      <rect fill="#FFB900" height="9" width="9" x="11" y="11" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="20"
      viewBox="0 0 48 48"
      width="20"
    >
      <path
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
        fill="#4285F4"
      />
      <path
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
        fill="#34A853"
      />
      <path
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
        fill="#FBBC05"
      />
      <path
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AuthFrame({ children }: { readonly children: ReactNode }) {
  return (
    <ObsinaProvider locale={APP_LOCALE} messages={APP_DESIGN_SYSTEM_MESSAGES}>
      <div className="auth-shell">
        <header className="app-header">
          <div className="app-header__inner">
            <div className="brand-lockup">
              <Wordmark />
              <span>{enMessages.auth.companyMemory}</span>
            </div>
          </div>
        </header>
        <div className="auth-panel">{children}</div>
      </div>
    </ObsinaProvider>
  );
}

async function signInWithProvider(
  supabase: SupabaseClient,
  provider: "azure" | "google",
  failureMessage: string,
  setError: (message: string | null) => void,
): Promise<void> {
  setError(null);
  // Login-only: no access_type=offline or prompt=consent. Neither provider's
  // refresh token is needed for sign-in, so neither is requested here.
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: window.location.origin,
      scopes: "openid profile email",
      queryParams: { prompt: "select_account" },
    },
  });
  if (error) {
    setError(failureMessage);
  }
}

export async function signInWithMicrosoft(
  supabase: SupabaseClient,
  setError: (message: string | null) => void,
): Promise<void> {
  return signInWithProvider(
    supabase,
    "azure",
    enMessages.auth.microsoftSignInFailed,
    setError,
  );
}

export async function signInWithGoogle(
  supabase: SupabaseClient,
  setError: (message: string | null) => void,
): Promise<void> {
  return signInWithProvider(
    supabase,
    "google",
    enMessages.auth.googleSignInFailed,
    setError,
  );
}

export async function signOut(
  apiBase: string,
  supabase: SupabaseClient,
): Promise<void> {
  await clearApplicationSession(apiBase);
  const { error } = await supabase.auth.signOut({ scope: "local" });
  if (!error) return;

  // The pinned auth-js version removes the local session before reporting
  // some remote sign-out errors. Confirm that security-relevant local state is
  // gone so the caller can reset the bridge and hide the authenticated UI.
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session === null) return;
  } catch {
    // Without a confirmed local removal the logout must remain retryable.
  }
  throw error;
}

function accountFromSession(session: Session): AuthenticatedAccount {
  const metadata = session.user.user_metadata;
  const metadataName = [metadata.full_name, metadata.name].find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );
  const trimmedEmail = session.user.email?.trim();
  const email = trimmedEmail && trimmedEmail.length > 0 ? trimmedEmail : null;
  const fallbackName = email?.split("@")[0] ?? "Obsina hesabı";
  return {
    userId: session.user.id,
    name: (metadataName?.trim() ?? fallbackName).slice(0, 160),
    email: email?.slice(0, 320) ?? null,
  };
}

interface AuthStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function providerTokenStrippingStorage(
  storage: AuthStorage,
): AuthStorage {
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => storage.setItem(key, stripProviderTokens(value)),
    removeItem: (key) => storage.removeItem(key),
  };
}

function stripProviderTokens(value: string): string {
  try {
    return JSON.stringify(stripProviderTokenFields(JSON.parse(value)));
  } catch {
    return value;
  }
}

function stripProviderTokenFields(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripProviderTokenFields);
  if (typeof value !== "object" || value === null) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(
        ([key]) => key !== "provider_token" && key !== "provider_refresh_token",
      )
      .map(([key, nested]) => [key, stripProviderTokenFields(nested)]),
  );
}
